// Per-layout content schemas. One discriminated-union variant per `<type>`
// value the Player recognises (16 layouts).
//
// Source of truth: wiki/framework_spec/13-layout-tag-catalogue.md §3 — each
// layout's "Editor controls" table is the canonical field list. Field names
// in this file equal XML tag names per the catalogue's §0.3 field-naming rule.
//
// **Vertical-slice scope.** This file currently implements `fullscreen_text_and_image`
// only — the smallest end-to-end loop. The other 15 layouts will land
// incrementally as the SCORM-emit path is proven. The `LayoutContent`
// discriminated union widens as each variant is added; no other code should
// need changes when a layout is added (the emitter switch will fail
// compilation, which is the desired forcing function).
import { z } from "zod";
import { BlankableLocalizedStringSchema, BlockingSectionSchema, ColorSchema, LocalizedStringSchema, LocalizedUrlSchema, MediaDescriptionSchema, UrlSchema, } from "./shared.js";
/**
 * Layout 3.1 — `fullscreen_text_and_image`.
 *
 * A full-screen module opener: title + body text over a background image.
 * Per catalogue §3.1's Editor controls table:
 *   Title, Title colour, Body text, Body text colour, Background image,
 *   Progress gate, Gate background, Gate text colour.
 *
 * No Subtitle, no title background.
 *
 * `contentBackgroundColor` is OPTIONAL and DOES render (Omar, 2026-08-05 —
 * "the full screen text and image can still have a plain color as background").
 * The layout catalogue §1.4 recorded it as "silently ignored" for this type,
 * which was true of the runtime as shipped but was never a design decision:
 * `fullscreen_text_and_image_template` simply omitted the one statement its six
 * sibling templates all carry —
 *
 *     $commonTemplate.find('.layoutContentContainer')
 *       .css('background-color', layout.contentBackgroundColor || '')   // ← this
 *       .html(currentTemplate);
 *
 * — so the value parsed into `layout` and was then never read. Fixed in the
 * pinned runtime; an export therefore MUST be built against a runtime carrying
 * that patch, or the colour silently does nothing again.
 *
 * The colour paints `.layoutContentContainer`, the PARENT of the
 * `.contentContainer` that carries the background image. Both may be set; the
 * image covers the colour, exactly as on the sibling layouts. The editor
 * nevertheless clears one when the author picks the other, so the preview and
 * the package can never disagree about which is visible.
 *
 * `backgroundImageUrl` is OPTIONAL (Omar, 2026-08-05: an export must not be
 * blocked for it — "the full screen text and image can still have a plain
 * color as background"). Read out of the shipped Player rather than assumed —
 * `fullscreen_text_and_image_template` in the package's own `js/components.js`
 * does:
 *
 *     var backgroundImageUrl = layout.backgroundImageUrl || '';
 *     … backgroundImageUrl ? `style="background-image: url('content/…')"` : ''
 *
 * so an absent value emits NO inline style and `.contentContainer` — which
 * declares background position/repeat/size but no background-COLOUR — is simply
 * transparent. Graceful degradation, not breakage, so requiring it was a
 * blocking error for a screen the runtime renders fine.
 *
 * ⚠️ The emitter MUST omit the tag entirely when this is absent. Emitting an
 * empty or "undefined" body would be TRUTHY in the expression above and the
 * Player would request `content/<lang>/undefined` and 404 — trading a clear
 * authoring error for a silent broken image
 * (see emit-layout.ts:emitFullscreenTextAndImage).
 */
export const FullscreenTextAndImageContentSchema = z
    .object({
    type: z.literal("fullscreen_text_and_image"),
    titleMain: BlankableLocalizedStringSchema,
    titleTextColor: ColorSchema,
    text: LocalizedStringSchema,
    textColor: ColorSchema,
    contentBackgroundColor: ColorSchema.optional(),
    backgroundImageUrl: UrlSchema.optional(),
    /** Screen-reader description — rendered as a real <img>, not a CSS background. Per language, never required,
     *  exempt from the translation gate (OQ-095 D1/D2/D7). */
    backgroundImageAlt: MediaDescriptionSchema.optional(),
    blockingSection: BlockingSectionSchema,
})
    .strict();
/**
 * A note on richtext fields (`text`, `leftColumnContent`, `rightColumnContent`):
 *   The catalogue defines these as `richtext`, but for cross-language
 *   support the editor surfaces them as a `LocalizedStringEditor`. The
 *   stored shape is therefore `{ [lang]: richtext }`. The XML emitter
 *   picks the right language at emit time and wraps the value in CDATA.
 */
/**
 * Layout 3.3 — `text_and_image` — one row.
 *
 * A single text+media row. Per catalogue §3.3 each row carries EITHER an
 * image (this schema) OR a video (sub-schema §4.4, deferred). v1 ships the
 * image-only row shape; the video form is a follow-up that lands together
 * with `fullscreen_video` / `small_video`.
 */
/**
 * Image-row variant of `<row>` inside `text_and_image`. Carries an
 * `imageUrl` flat on the row (NOT nested in a `<image>` wrapper — the
 * real XML at `raw/ux-references/text_and_image_left_right.txt` confirms
 * the flat shape).
 */
export const TextAndImageImageRowSchema = z
    .object({
    /** Discriminator. The author picks "image" or "video" in the row's media-kind segmented control per catalogue §3.3. */
    kind: z.literal("image"),
    backgroundColor: ColorSchema,
    imageUrl: UrlSchema,
    /** Screen-reader description — the row image a learner is shown. Per language, never required,
     *  exempt from the translation gate (OQ-095 D1/D2/D7). */
    imageAlt: MediaDescriptionSchema.optional(),
    text: LocalizedStringSchema,
    textColor: ColorSchema,
    imageOnTheLeft: z.boolean(),
})
    .strict();
/**
 * Video-row variant of `<row>` inside `text_and_image`. Carries
 * `videoUrl` + `videoThumbUrl` flat on the row (NOT nested in a
 * `<video>` wrapper — confirmed against the real production fragment at
 * `raw/ux-references/text_and_image_video.txt`). `subtitlesUrl` is
 * always emitted (possibly empty) per the same demo fragment so the
 * Player's parser finds the expected node shape.
 *
 * Added 2026-06-05 per locked-in design at
 * [`wiki/Prototype_schema_migration.md`](../../../Dynamo_Authoring_Wiki/wiki/Prototype_schema_migration.md)
 * Fix 7 (Omar's "implement it now" 2026-06-04). Catalogue §3.3 already
 * documents the per-row media-kind segmented control in the Editor
 * controls table.
 */
export const TextAndImageVideoRowSchema = z
    .object({
    /** Discriminator. The author picks "video" in the row's media-kind segmented control. */
    kind: z.literal("video"),
    backgroundColor: ColorSchema,
    videoUrl: UrlSchema,
    /** Screen-reader description — the VIDEO carries the description, never its poster (D4). Per language, never required,
     *  exempt from the translation gate (OQ-095 D1/D2/D7). */
    videoAlt: MediaDescriptionSchema.optional(),
    videoThumbUrl: UrlSchema.optional(),
    subtitlesUrl: LocalizedUrlSchema.optional(),
    text: LocalizedStringSchema,
    textColor: ColorSchema,
    imageOnTheLeft: z.boolean(),
})
    .strict();
/**
 * A single row inside `text_and_image`. Discriminated by `kind` — image
 * or video. Catalogue §3.3 Editor controls table shows the per-row
 * media-kind segmented control + the fields that appear "when image" vs
 * "when video".
 */
export const TextAndImageRowSchema = z.discriminatedUnion("kind", [
    TextAndImageImageRowSchema,
    TextAndImageVideoRowSchema,
]);
/**
 * Layout 3.3 — `text_and_image`.
 *
 * A stack of alternating text+image rows (image left or right per row).
 * Per catalogue §3.3's Editor controls table. Image-only rows only in v1;
 * the video-row variant lives in sub-schema §4.4 (deferred — see
 * `TextAndImageRowSchema`).
 */
export const TextAndImageContentSchema = z
    .object({
    type: z.literal("text_and_image"),
    titleMain: BlankableLocalizedStringSchema,
    titleSub: LocalizedStringSchema.optional(),
    titleTextColor: ColorSchema,
    titleBackgroundColor: ColorSchema.optional(),
    rows: z.array(TextAndImageRowSchema).min(1),
    blockingSection: BlockingSectionSchema,
})
    .strict();
/**
 * Layout 3.16 — `title`.
 *
 * A title-only slide / section divider. The smallest layout in the kit.
 * Per catalogue §3.16's Editor controls table.
 *
 * No body text, no two-pane content. Optional `titleSub`,
 * `titleBackgroundColor`, and `backgroundImageUrl` may be omitted by the
 * author; the emitter writes only the children that are present.
 */
export const TitleContentSchema = z
    .object({
    type: z.literal("title"),
    titleMain: BlankableLocalizedStringSchema,
    titleSub: LocalizedStringSchema.optional(),
    titleTextColor: ColorSchema,
    titleBackgroundColor: ColorSchema.optional(),
    backgroundImageUrl: UrlSchema.optional(),
    blockingSection: BlockingSectionSchema,
})
    .strict();
/**
 * Layout 3.12 — `two_columns_text`.
 *
 * Two side-by-side rich-text columns, no media. Per catalogue §3.12's
 * Editor controls table. Both columns' text, text colour, and background
 * are required.
 *
 * `leftColumnContent` / `rightColumnContent` are stored as
 * `LocalizedString` (per-language richtext) per the note above.
 */
export const TwoColumnsTextContentSchema = z
    .object({
    type: z.literal("two_columns_text"),
    titleMain: BlankableLocalizedStringSchema,
    titleSub: LocalizedStringSchema.optional(),
    titleTextColor: ColorSchema,
    titleBackgroundColor: ColorSchema.optional(),
    leftColumnContent: LocalizedStringSchema,
    leftColumnTextColor: ColorSchema,
    leftColumnBackgroundColor: ColorSchema,
    rightColumnContent: LocalizedStringSchema,
    rightColumnTextColor: ColorSchema,
    rightColumnBackgroundColor: ColorSchema,
    blockingSection: BlockingSectionSchema,
})
    .strict();
/**
 * Sub-schema §4.1 — In-video `<interactions>` overlays.
 *
 * Timed overlays that appear at a video timecode inside a host video
 * layout. Three interaction `<type>` values — `discover` / `question` /
 * `mandatoryQuestion` — per catalogue §4.1 and runtime registry per
 * wiki/framework_spec/14-tag-to-runtime-map.md §5.1.
 *
 * The same in-video interaction is supported by `fullscreen_video` /
 * `small_video` / video tabs inside `sequence` / `horizontal_tabs`. This
 * file plugs the field into `SmallVideoContentSchema`; future host
 * layouts reuse the exact same `InteractionSchema`.
 *
 * **Important** — `discover` / `question` / `mandatoryQuestion` cannot
 * live as standalone layouts (`<type>` inside a top-level `<layout>`);
 * they only render as in-video overlays via this sub-schema. See
 * 14-tag-to-runtime-map.md §2 for the dispatcher's 13-layout registry.
 */
export const InteractionOptionSchema = z
    .object({
    text: LocalizedStringSchema,
    isCorrect: z.boolean().optional(),
})
    .strict();
/**
 * Feedback pair shown by a `mandatoryQuestion` overlay (correct + wrong
 * messages). `question` overlays do not carry feedback per catalogue §4.1.
 */
export const InteractionFeedbacksSchema = z
    .object({
    correct: LocalizedStringSchema,
    wrong: LocalizedStringSchema,
})
    .strict();
/**
 * `discover` interaction — a timed `<plus>` hotspot that opens a reveal
 * panel of title + body. `timeEnds` is required (every demo discover
 * fragment includes it).
 *
 * **`timeStarts` and `timeEnds` accept fractional seconds** (e.g. `3.5`
 * for 3.5 seconds, `12.25` for 12 seconds and a quarter). The Player's
 * `timeupdate` handler parses both via `parseFloat` (since the
 * 2026-06-02 runtime patch — see [[bug-log]]); precision is bounded by
 * the video's frame rate (~16 ms at 60 fps, ~33 ms at 30 fps). Production
 * content historically used integer seconds; fractional precision is a
 * forward-compatible addition.
 */
export const InteractionDiscoverSchema = z
    .object({
    type: z.literal("discover"),
    timeStarts: z.number().nonnegative(),
    timeEnds: z.number().nonnegative(),
    bgColor: ColorSchema.optional(),
    borderColor: ColorSchema.optional(),
    textColor: ColorSchema.optional(),
    interactionText: LocalizedStringSchema,
    descriptionTitle: LocalizedStringSchema,
    descriptionText: LocalizedStringSchema,
})
    .strict();
/**
 * `question` interaction — a non-blocking inline question (learner may
 * advance without answering). Prompt `text` is optional per demos
 * (05_QO ships question fragments without a prompt). Options
 * required (min 2).
 *
 * **No `timeEnds` field — deliberate, enforced.** Per catalogue §4.1 +
 * runtime-map §5.1, `question` interactions terminate on **user action**
 * (click an option), NOT on time-window expiry. The Player's user-action
 * hide handler (`components.js:3397`) schedules a 1500 ms `setTimeout` to
 * clear `currentlyShowing[index]` after the click. If `timeEnds` were set
 * wider than `timeStarts + 1`, the dispatcher would re-fire the show
 * condition once the flag clears — the bug we burned a day on
 * 2026-05-31 ([[../../../Dynamo_Authoring_Wiki/wiki/findings/bug-log.md]]).
 * Setting `timeEnds` is also rejected by the `.strict()` + the
 * `InteractionSchema` superRefine below (with a helpful error).
 */
export const InteractionQuestionSchema = z
    .object({
    type: z.literal("question"),
    timeStarts: z.number().nonnegative(),
    bgColor: ColorSchema.optional(),
    textColor: ColorSchema.optional(),
    text: LocalizedStringSchema.optional(),
    options: z.array(InteractionOptionSchema).min(2),
})
    .strict();
/**
 * `mandatoryQuestion` interaction — blocking question; learner MUST
 * answer to proceed. Prompt `text` is REQUIRED (all observed demos
 * carry it). Optional `feedbacks` block with correct + wrong messages.
 *
 * **No `timeEnds` field — deliberate, enforced.** Same rationale as
 * `InteractionQuestionSchema` above: this type terminates on user
 * action (click correct option + click "continue"), not on time-window
 * expiry. The 1500 ms grace period in the Player's hide handler aligns
 * with the dispatcher's default 1-second show window; a wider explicit
 * `timeEnds` breaks that alignment and re-fires.
 */
export const InteractionMandatoryQuestionSchema = z
    .object({
    type: z.literal("mandatoryQuestion"),
    timeStarts: z.number().nonnegative(),
    bgColor: ColorSchema.optional(),
    textColor: ColorSchema.optional(),
    text: LocalizedStringSchema,
    options: z.array(InteractionOptionSchema).min(2),
    feedbacks: InteractionFeedbacksSchema.optional(),
})
    .strict();
/**
 * Discriminated union over the three in-video interaction types.
 * The emitter's per-type dispatch will fail compilation when a new
 * variant is added without a matching case (the desired forcing
 * function, same pattern as `LayoutContentSchema`).
 *
 * **superRefine note.** Adding `timeEnds` to a `question` or
 * `mandatoryQuestion` is rejected with a clear error message rather
 * than the generic `Unrecognized key(s)` from `.strict()`. The check
 * is redundant with `.strict()` for primary protection but makes the
 * invariant findable when someone reads the schema (and the error
 * tells the next author *why* it's forbidden, not just *that* it is).
 */
export const InteractionSchema = z
    .discriminatedUnion("type", [
    InteractionDiscoverSchema,
    InteractionQuestionSchema,
    InteractionMandatoryQuestionSchema,
])
    .superRefine((val, ctx) => {
    if ((val.type === "question" || val.type === "mandatoryQuestion") &&
        "timeEnds" in val) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["timeEnds"],
            message: `\`timeEnds\` is not allowed on \`${val.type}\` interactions — these terminate on user action (click an option / click continue), not on time-window expiry. The Player's user-action hide handler clears state after 1500 ms; a wider \`timeEnds\` re-fires the interaction. Drop \`timeEnds\` entirely. See wiki/findings/bug-log.md (2026-05-31 content-shape invariant section).`,
        });
    }
});
/**
 * Layout 3.4 — `small_video`.
 *
 * An inline (non-fullscreen) video block. Per catalogue §3.4's Editor
 * controls table. `videoUrl` is required; `videoThumbUrl` (the poster on
 * the play button) is optional — the Player shows the play button with no
 * background image when it's absent (verified against `components.js`;
 * loosened 2026-06-17 to un-block media-upload drafts that supply a video
 * but no separate poster). The title block, `contentBackgroundColor`,
 * `subtitlesUrl`, and `transcriptUrl`
 * are all optional (real fragments often leave most of the title block
 * empty; demos do populate them, but the spec lets them be omitted).
 *
 * **`<interactions>` in-video overlays** are now modelled via the
 * shared `InteractionSchema` above (catalogue §4.1, runtime §5.1). The
 * field is optional — a `small_video` with no overlays still emits a
 * plain playable video.
 */
export const SmallVideoContentSchema = z
    .object({
    type: z.literal("small_video"),
    titleMain: LocalizedStringSchema.optional(),
    titleSub: LocalizedStringSchema.optional(),
    titleTextColor: ColorSchema.optional(),
    titleBackgroundColor: ColorSchema.optional(),
    contentBackgroundColor: ColorSchema.optional(),
    videoUrl: UrlSchema,
    /** Screen-reader description — the VIDEO carries the description, never its poster (D4). Per language, never required,
     *  exempt from the translation gate (OQ-095 D1/D2/D7). */
    videoAlt: MediaDescriptionSchema.optional(),
    videoThumbUrl: UrlSchema.optional(),
    subtitlesUrl: LocalizedUrlSchema.optional(),
    transcriptUrl: LocalizedUrlSchema.optional(),
    interactions: z.array(InteractionSchema).optional(),
    blockingSection: BlockingSectionSchema,
})
    .strict();
/**
 * Layout 3.2 — `fullscreen_video`.
 *
 * A full-screen video with a title block above it. Per catalogue §3.2's
 * Editor controls table. Reuses the shared `InteractionSchema` for
 * in-video overlays (catalogue §4.1, runtime §5.1).
 *
 * **Differences vs. `small_video`:**
 * - `titleMain` and `titleTextColor` are **required keys** (the title bar is a
 *   structural element of this layout, not optional decoration) — where
 *   `small_video` may omit `titleMain` entirely. Required key, not required
 *   TEXT: like every layout title it is `BlankableLocalizedStringSchema`, so an
 *   author who wants no title bar leaves it blank and the Player renders none.
 * - `contentBackgroundColor` is intentionally **omitted** — the Player
 *   silently ignores it for `fullscreen_video` because the video fills
 *   the content area (catalogue §3.2 + §1.4 clarification). `.strict()`
 *   rejects it at parse time so authors get a clear error rather than
 *   wondering why their colour didn't apply.
 *
 * Other fields match `small_video` — same media trio, same optional
 * subtitles / transcript, same `<interactions>` sub-schema, same
 * blocking-section trio.
 */
export const FullscreenVideoContentSchema = z
    .object({
    type: z.literal("fullscreen_video"),
    titleMain: BlankableLocalizedStringSchema,
    titleSub: LocalizedStringSchema.optional(),
    titleTextColor: ColorSchema,
    titleBackgroundColor: ColorSchema.optional(),
    videoUrl: UrlSchema,
    /** Screen-reader description — the VIDEO carries the description, never its poster (D4). Per language, never required,
     *  exempt from the translation gate (OQ-095 D1/D2/D7). */
    videoAlt: MediaDescriptionSchema.optional(),
    videoThumbUrl: UrlSchema.optional(),
    subtitlesUrl: LocalizedUrlSchema.optional(),
    transcriptUrl: LocalizedUrlSchema.optional(),
    interactions: z.array(InteractionSchema).optional(),
    blockingSection: BlockingSectionSchema,
})
    .strict();
/**
 * Layout 3.5 — `sequence` — sub-types.
 *
 * A `sequence` layout is a stepped tour over a heterogeneous list of
 * `<tab>` elements. Per catalogue §3.5 + the canonical Quiz_layouts_options.md
 * (wiki) §3.3–§3.6, there are FOUR kinds of tab:
 *
 *   - `text`     — image background + body text overlay
 *   - `video`    — full video with optional in-video `<interactions>` (§4.1)
 *   - `question` — image background + a quiz prompt + answers. XML emission
 *                  picks `<questionShort>` / `<question>` / `<questionMultiple>`
 *                  at emit time based on `options.length` + correct-tick count
 *                  (see Quiz_layouts_options.md §6.1 / §8.1).
 *   - `feedback` — image background + branching feedback text. **No editable
 *                  text on this tab itself** — the emitter sources the
 *                  feedback bodies from the preceding `question` tab's
 *                  `options[].feedback` (single-choice) or `feedbacks.{correct,wrong}`
 *                  (multi-choice). Per the prototype's §17 step-pair binding
 *                  rule, a feedback tab is always immediately preceded by
 *                  its paired question tab.
 *
 * The `kind` field is the schema-side discriminator (independent of the
 * XML element names, which differ per kind).
 */
/** One answer option inside a sequence `question` tab. */
export const SequenceQuestionOptionSchema = z
    .object({
    text: LocalizedStringSchema,
    isCorrect: z.boolean().optional(),
    /**
     * Per-answer feedback text. Used when the question is single-choice
     * (0 or 1 correct tick across all options) and emitted as
     * `<text for="N">` on the paired feedback tab. Ignored when the
     * question is multi-choice — see `feedbacks` on
     * `SequenceQuestionTabSchema`.
     */
    feedback: LocalizedStringSchema.optional(),
})
    .strict();
/**
 * A step's label for a phone, ONE PER LANGUAGE (Plan 37 ③, Omar, 2026-10-26: "Sequence phone label: one per language
 * in the server and the package. It should be the same text that Course architecture calls "Mobile label" and
 * Localisation calls "Tab Title"."). The sequence editor's "Mobile label" box has always written it per language; this
 * list had no place for it, so the clean-up deleted it and every language's package carried the one `mobileText`
 * (measured on QA: a German learner got "New video step", not "Neuer Videoschritt").
 *
 * `mobileText` stays: the app still sends it (the label in one language), it is what a course saved before this
 * holds, and the emitter falls back to it when `tabTitle` is absent. The emitter writes `<mobileText>` from
 * `tabTitle[language]` when the step has one (`emit-layout.ts` `sequenceMobileText`).
 */
const SequenceStepLabelSchema = LocalizedStringSchema.optional();
/** A `text` tab — image canvas + body text overlay. */
export const SequenceTextTabSchema = z
    .object({
    kind: z.literal("text"),
    mobileText: z.string().optional(),
    tabTitle: SequenceStepLabelSchema,
    imageUrl: UrlSchema,
    /** Screen-reader description — the tab image a learner is shown. Per language, never required,
     *  exempt from the translation gate (OQ-095 D1/D2/D7). */
    imageAlt: MediaDescriptionSchema.optional(),
    text: LocalizedStringSchema,
})
    .strict();
/**
 * A `video` tab — same media + optional in-video interactions as
 * `small_video` / `fullscreen_video`. Reuses the shared
 * `InteractionSchema` (`mandatoryQuestion`, `question`, `discover`)
 * unchanged.
 */
export const SequenceVideoTabSchema = z
    .object({
    kind: z.literal("video"),
    mobileText: z.string().optional(),
    tabTitle: SequenceStepLabelSchema,
    videoUrl: UrlSchema,
    /** Screen-reader description — the VIDEO carries the description, never its poster (D4). Per language, never required,
     *  exempt from the translation gate (OQ-095 D1/D2/D7). */
    videoAlt: MediaDescriptionSchema.optional(),
    videoThumbUrl: UrlSchema.optional(),
    subtitlesUrl: LocalizedUrlSchema.optional(),
    transcriptUrl: LocalizedUrlSchema.optional(),
    interactions: z.array(InteractionSchema).optional(),
})
    .strict();
/**
 * A `question` tab — image canvas + question prompt + answers.
 *
 * The XML element emitted (`<questionShort>` vs `<question>` vs
 * `<questionMultiple>`) is **auto-derived** at emit time from the
 * authoring data (`options.length` + correct-tick count):
 *
 *   - `correctCount === 0 || 1` AND `options.length === 2` → `<questionShort>`
 *   - `correctCount === 0 || 1` AND `options.length >= 3`  → `<question>`
 *   - `correctCount >= 2`                                  → `<questionMultiple>`
 *
 * Authoring data shape (per Quiz_layouts_options.md §5):
 * - `options[].feedback` — populated when the question is single-choice
 *   (correctCount ≤ 1). Emitted as `<text for="N">` on the paired
 *   feedback tab.
 * - `feedbacks` — populated when the question is multi-choice
 *   (correctCount ≥ 2). Emitted as `<text for="correct">` +
 *   `<text for="wrong">` on the paired feedback tab.
 *
 * The superRefine on `SequenceContentSchema` enforces that the right
 * feedback shape is filled in based on the correct-tick count, and
 * that every question tab is immediately followed by its paired
 * feedback tab.
 */
export const SequenceQuestionTabSchema = z
    .object({
    kind: z.literal("question"),
    mobileText: z.string().optional(),
    tabTitle: SequenceStepLabelSchema,
    imageUrl: UrlSchema,
    /** Screen-reader description — the question image; the same tab path as a text tab. Per language, never required,
     *  exempt from the translation gate (OQ-095 D1/D2/D7). */
    imageAlt: MediaDescriptionSchema.optional(),
    text: LocalizedStringSchema,
    bgColor: ColorSchema.optional(),
    textColor: ColorSchema.optional(),
    btnBgColor: ColorSchema.optional(),
    btnTextColor: ColorSchema.optional(),
    options: z.array(SequenceQuestionOptionSchema).min(2),
    feedbacks: InteractionFeedbacksSchema.optional(),
})
    .strict();
/**
 * A `feedback` tab — paired with the immediately preceding `question`
 * tab. The tab itself carries only the background image; the feedback
 * text shown to the learner is sourced from the preceding question
 * tab's `options[].feedback` / `feedbacks.{correct,wrong}` at emit
 * time. The prototype's §17 step-pair binding ensures the editor
 * cascades creation/deletion across the pair.
 */
export const SequenceFeedbackTabSchema = z
    .object({
    kind: z.literal("feedback"),
    mobileText: z.string().optional(),
    tabTitle: SequenceStepLabelSchema,
    imageUrl: UrlSchema,
    /**
     * Screen-reader description for the image behind the feedback card.
     *
     * Added 2026-09-02 after measuring that the Player DOES speak it: the
     * `sequence` template renders every tab through one loop and writes
     * `alt="…imageUrl['_alt']…"` regardless of `kind`, with no enclosing
     * `aria-hidden` (`feedback_a_written_alt_is_not_a_spoken_one`). The
     * feedback tab was the one media site in the layout still emitting a
     * bare `<imageUrl>`; the gap was authoring-side, not a runtime limit.
     *
     * Per language, never required, never gated — see `MediaDescriptionSchema`.
     */
    imageAlt: MediaDescriptionSchema.optional(),
})
    .strict();
/** A `sequence` tab — discriminated by `kind`. */
export const SequenceTabSchema = z.discriminatedUnion("kind", [
    SequenceTextTabSchema,
    SequenceVideoTabSchema,
    SequenceQuestionTabSchema,
    SequenceFeedbackTabSchema,
]);
/**
 * Layout 3.5 — `sequence`.
 *
 * Per catalogue §3.5's Editor controls table + Quiz_layouts_options.md §6/§8.
 *
 * The `superRefine` enforces the schema-side validation rules from
 * Quiz_layouts_options.md §8.4:
 *   1. Every `question` tab must be immediately followed by a
 *      `feedback` tab (the §17 step-pair binding).
 *   2. Every `feedback` tab must be immediately preceded by a
 *      `question` tab (it has no text source otherwise).
 *   3. Single-choice question (`correctCount ≤ 1`): every option
 *      must have a non-empty `feedback` field (sourced into
 *      `<text for="N">`).
 *   4. Multi-choice question (`correctCount ≥ 2`): `feedbacks.correct`
 *      and `feedbacks.wrong` must be present (sourced into
 *      `<text for="correct">` and `<text for="wrong">`).
 *   5. Multi-choice question additionally requires at least one
 *      `isCorrect: true` AND at least one `isCorrect: false` option
 *      (a multi-choice quiz where every option is right is degenerate).
 */
export const SequenceContentSchema = z
    .object({
    type: z.literal("sequence"),
    titleMain: BlankableLocalizedStringSchema,
    titleSub: LocalizedStringSchema.optional(),
    titleTextColor: ColorSchema,
    titleBackgroundColor: ColorSchema.optional(),
    contentBackgroundColor: ColorSchema.optional(),
    dotSelectedColor: ColorSchema.optional(),
    tabs: z.array(SequenceTabSchema).min(1),
    blockingSection: BlockingSectionSchema,
})
    .strict();
/**
 * Run the §8.4 schema-side validation rules on a SequenceContent value.
 *
 * Kept separate from `SequenceContentSchema.superRefine(...)` because
 * `z.discriminatedUnion` requires its members to be `ZodObject` (a
 * `superRefine` chain returns `ZodEffects`, which the union rejects).
 * Instead the rules live on the top-level `LayoutContentSchema` union's
 * superRefine — but factored out here for reuse by any caller that
 * wants to validate a sequence in isolation.
 */
function validateSequenceContent(val, ctx) {
    val.tabs.forEach((tab, i) => {
        if (tab.kind === "question") {
            validateQuestionTab(tab, i, val.tabs, ctx);
        }
        else if (tab.kind === "feedback") {
            const prevTab = val.tabs[i - 1];
            if (!prevTab || prevTab.kind !== "question") {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["tabs", i],
                    message: `Sequence \`feedback\` tab at index ${i} must be immediately preceded by a \`question\` tab — it has no text source otherwise. See wiki/Quiz_layouts_options.md §7 + prototype docs §17 (step-pair binding).`,
                });
            }
        }
    });
}
function validateQuestionTab(tab, i, tabs, ctx) {
    // Rule 1: question must be followed by a feedback tab.
    const nextTab = tabs[i + 1];
    if (!nextTab || nextTab.kind !== "feedback") {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["tabs", i],
            message: `Sequence \`question\` tab at index ${i} must be immediately followed by a \`feedback\` tab. The feedback tab sources its text from this question at emit time. See wiki/Quiz_layouts_options.md §7.`,
        });
    }
    const correctCount = tab.options.filter((o) => o.isCorrect === true).length;
    if (correctCount >= 2) {
        // Rules 4 + 5: multi-choice requires feedbacks + at least one wrong.
        if (!tab.feedbacks) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["tabs", i, "feedbacks"],
                message: `Multi-choice sequence question (2+ correct ticks) requires \`feedbacks.correct\` and \`feedbacks.wrong\` — emitted as <text for="correct"> / <text for="wrong"> on the paired feedback tab. See wiki/Quiz_layouts_options.md §6.1.`,
            });
        }
        const wrongCount = tab.options.length - correctCount;
        if (wrongCount === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["tabs", i, "options"],
                message: `Multi-choice sequence question requires at least one option with \`isCorrect: false\` (a quiz where every option is correct is degenerate).`,
            });
        }
    }
    else {
        // Rule 3: single-choice requires per-answer feedback.
        tab.options.forEach((opt, j) => {
            if (!opt.feedback || Object.keys(opt.feedback).length === 0) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["tabs", i, "options", j, "feedback"],
                    message: `Single-choice sequence question (0 or 1 correct ticks) requires per-answer \`feedback\` on every option — emitted as <text for="${j + 1}"> on the paired feedback tab. See wiki/Quiz_layouts_options.md §6.1.`,
                });
            }
        });
    }
}
/**
 * `SequenceContentSchema` + the §8.4 validation rules. Use this when
 * validating a sequence layout in isolation; the top-level
 * `LayoutContentSchema` union runs the same rules automatically.
 */
export const SequenceContentSchemaValidated = SequenceContentSchema.superRefine(validateSequenceContent);
/**
 * Layout 3.7 — `vertical_tabs` — one tab.
 *
 * Per catalogue §3.7 + §1.10 (shared tab block). Each tab carries only a
 * `tabTitle` (plain text — emitted xml-escaped) and a `tabText` (richtext
 * — emitted CDATA-wrapped). No media, no per-tab colours — the simplest
 * tab shape in the kit.
 */
export const VerticalTabsTabSchema = z
    .object({
    tabTitle: LocalizedStringSchema,
    tabText: LocalizedStringSchema,
})
    .strict();
/**
 * Layout 3.7 — `vertical_tabs`.
 *
 * A vertical tab strip — title block, optional content background and
 * background image, then a list of tabs where each tab is just a title +
 * a rich-text body. No media, no in-video interactions. Per catalogue
 * §3.7's Editor controls table.
 *
 * **`selectedBgColor` added 2026-08-26 — the anticipated additive change.**
 * This block previously read "Selection colours deliberately omitted",
 * and closed by saying that adding them later was "a strict-additive
 * change … once a real course needs custom selected-tab styling". Omar
 * asked for exactly that: *"There should be next to the EXTERNAL BG
 * colour picker also the colour picker to define the BG colour of the
 * tabs when they are selected."* So one of the three is now surfaced.
 *
 * No Player change and no re-pin: `vertical_tabs_template` already
 * writes the value onto `.tabsNavigation` as `data-selectedbgcolor`
 * (`dynamo-player/js/components.js:5743`), and
 * `horizontal_tabs_setActiveTab` (:4420 — vertical tabs share it) reads
 * that attribute back and applies it as an inline
 * `background-color` + `border-color` on the active tab. Absent, the
 * inline style is `background-color: ;` — invalid, dropped — and the
 * stylesheet's `.tabNavElement.active { background-color:#5514b4 }`
 * wins, which is BT's purple.
 *
 * **`selectedTextColor` and `tabTextColor` stay unsurfaced.** The
 * runtime reads both, but Omar asked for one control and the ink half
 * belongs with the brand-palette work rather than a second swatch here
 * (see `sample-content.jsx`'s "Not seeded" policy). Adding either later
 * remains the same strict-additive change this note used to describe.
 */
export const VerticalTabsContentSchema = z
    .object({
    type: z.literal("vertical_tabs"),
    titleMain: BlankableLocalizedStringSchema,
    titleSub: LocalizedStringSchema.optional(),
    titleTextColor: ColorSchema,
    contentBackgroundColor: ColorSchema.optional(),
    backgroundImageUrl: UrlSchema.optional(),
    /** Screen-reader description — rendered as a real <img>, not a CSS background. Per language, never required,
     *  exempt from the translation gate (OQ-095 D1/D2/D7). */
    backgroundImageAlt: MediaDescriptionSchema.optional(),
    selectedBgColor: ColorSchema.optional(),
    tabs: z.array(VerticalTabsTabSchema).min(1),
    blockingSection: BlockingSectionSchema,
})
    .strict();
/**
 * Layout 3.8 — `icons_discover` — one icon tile.
 *
 * Per catalogue §3.8. Each tile pairs an `imageUrl` (the icon image) with
 * `text` (the reveal-text shown when the tile is clicked). The reveal
 * text is richtext (catalogue §2 inline HTML tags allowed); the editor
 * surfaces both fields via the standard widgets.
 */
export const IconsDiscoverIconSchema = z
    .object({
    imageUrl: UrlSchema,
    /** Screen-reader description — folded into the icon control’s accessible name. Per language, never required,
     *  exempt from the translation gate (OQ-095 D1/D2/D7). */
    imageAlt: MediaDescriptionSchema.optional(),
    text: LocalizedStringSchema,
})
    .strict();
/**
 * Layout 3.8 — `icons_discover`.
 *
 * A grid of clickable icons; clicking one reveals its text. Per catalogue
 * §3.8's Editor controls table.
 *
 * **Grid size — min 3, no upper bound.** A grid with 0/1/2 icons is
 * rejected (visually degenerate). No max enforced so future courses
 * can render >9 icons if their design calls for it — per Omar 2026-06-02
 * (the catalogue's "3 or more" guidance is recommendation, not invariant).
 */
export const IconsDiscoverContentSchema = z
    .object({
    type: z.literal("icons_discover"),
    titleMain: BlankableLocalizedStringSchema,
    titleSub: LocalizedStringSchema.optional(),
    titleTextColor: ColorSchema,
    titleBackgroundColor: ColorSchema.optional(),
    contentBackgroundColor: ColorSchema.optional(),
    selectedBgColor: ColorSchema.optional(),
    icons: z.array(IconsDiscoverIconSchema).min(3),
    blockingSection: BlockingSectionSchema,
})
    .strict();
/**
 * Layout 3.6 — `horizontal_tabs` — image tab.
 *
 * A tab carrying a still image + body text. Per catalogue §3.6 (the
 * "media kind" per-tab segmented control selects this vs. the video
 * shape). The `kind` discriminator is authoring-side only — the XML
 * has no `kind` element; the emitter routes by `kind` and emits the
 * appropriate per-tab child set.
 */
export const HorizontalTabsImageTabSchema = z
    .object({
    kind: z.literal("image"),
    tabTitle: LocalizedStringSchema,
    imageUrl: UrlSchema,
    /** Screen-reader description — the tab image a learner is shown. Per language, never required,
     *  exempt from the translation gate (OQ-095 D1/D2/D7). */
    imageAlt: MediaDescriptionSchema.optional(),
    tabText: LocalizedStringSchema,
})
    .strict();
/**
 * Layout 3.6 — `horizontal_tabs` — video tab.
 *
 * A tab carrying a video (with optional poster + optional subtitles + optional
 * transcript) + body text, plus optional in-video `<interactions>`
 * overlays. Reuses the shared `InteractionSchema` unchanged (catalogue
 * §4.1, runtime §5.1).
 */
export const HorizontalTabsVideoTabSchema = z
    .object({
    kind: z.literal("video"),
    tabTitle: LocalizedStringSchema,
    videoUrl: UrlSchema,
    /** Screen-reader description — the VIDEO carries the description, never its poster (D4). Per language, never required,
     *  exempt from the translation gate (OQ-095 D1/D2/D7). */
    videoAlt: MediaDescriptionSchema.optional(),
    videoThumbUrl: UrlSchema.optional(),
    subtitlesUrl: LocalizedUrlSchema.optional(),
    transcriptUrl: LocalizedUrlSchema.optional(),
    tabText: LocalizedStringSchema,
    interactions: z.array(InteractionSchema).optional(),
})
    .strict();
/** A `horizontal_tabs` tab — discriminated by `kind`. */
export const HorizontalTabsTabSchema = z.discriminatedUnion("kind", [
    HorizontalTabsImageTabSchema,
    HorizontalTabsVideoTabSchema,
]);
/**
 * Layout 3.6 — `horizontal_tabs`.
 *
 * A horizontal tab strip with **at least 3 tabs** (catalogue §3.6 +
 * raw fragment comment "at least 3 tabs needed"). Each tab is either
 * image or video; video tabs may carry in-video interactions. Per
 * catalogue §3.6's Editor controls table.
 *
 * Tag order matches `raw/ux-references/horizontal_tabs.txt`:
 * type, title block (titleMain req + titleSub opt + titleTextColor req
 * + titleBackgroundColor opt), tab-strip colour trio (selectedBgColor,
 * selectedTextColor, tabTextColor — all opt), the tabs array, the
 * blocking-section trio.
 */
export const HorizontalTabsContentSchema = z
    .object({
    type: z.literal("horizontal_tabs"),
    titleMain: BlankableLocalizedStringSchema,
    titleSub: LocalizedStringSchema.optional(),
    titleTextColor: ColorSchema,
    titleBackgroundColor: ColorSchema.optional(),
    selectedBgColor: ColorSchema.optional(),
    selectedTextColor: ColorSchema.optional(),
    tabTextColor: ColorSchema.optional(),
    tabs: z.array(HorizontalTabsTabSchema).min(3),
    blockingSection: BlockingSectionSchema,
})
    .strict();
/**
 * Sub-schema §4.2 — image-hotspot mini-quiz question.
 *
 * Used inside `<questions>` blocks that live on `hidden_items` per-hotspot
 * panels and on `quiz_images` (when that ships). Per catalogue §1.11 +
 * §4.2: each question carries a prompt, optional media (image), an
 * answers array with optional per-option `correct="true"` attribute, and
 * an optional `feedbackText`.
 *
 * **Why optional `imageUrl` here vs. always-required elsewhere.** Real
 * fragments (hidden_items 360° demo) show some mini-quiz questions
 * carry an image (wrapped at emit time in `<content><image><imageUrl>`)
 * and some don't. When `quiz_images` ships this schema may be widened
 * to also support `<video>` + `<html>` media kinds; for now (hidden_items
 * scope) image-or-none is sufficient.
 */
export const ImageHotspotMiniQuizAnswerSchema = z
    .object({
    text: LocalizedStringSchema,
    isCorrect: z.boolean().optional(),
})
    .strict();
export const ImageHotspotMiniQuizQuestionSchema = z
    .object({
    text: LocalizedStringSchema,
    imageUrl: UrlSchema.optional(),
    answers: z.array(ImageHotspotMiniQuizAnswerSchema).min(2),
    feedbackText: LocalizedStringSchema.optional(),
})
    .strict();
/**
 * Layout 3.9 — `hidden_items` — per-hotspot item.
 *
 * The shared §1.9 item/panel block. Each hotspot pairs a position with
 * a reveal panel that can carry text + optional image / video / mini-quiz.
 * Per catalogue §3.9 + §1.9.
 *
 * **`itemPosition` format depends on the host layout's variant.**
 * - Flat variant: `"x,y"` as percentages from top-left (0–100).
 * - 360° panorama variant: `"pitch,yaw"` in degrees (can be negative).
 *
 * The schema keeps `itemPosition` as a plain string — the Player parses
 * it differently per variant. Validation of the format itself is out of
 * scope; authors who write malformed positions get a Player render bug,
 * not a schema reject.
 *
 * **Deliberately deferred per Omar 2026-06-02:**
 * - Per-item `alt` attributes on `itemImageUrl` / `itemVideoThumbUrl`
 *   (catalogue notes them as optional; runtime reads them; no canonical
 *   field-name convention yet).
 * - `itemPositionMobile` (mobile-override) — runtime supports, raw
 *   fragments don't exercise it.
 */
export const HiddenItemsItemSchema = z
    .object({
    itemPosition: z.string(),
    itemTitle: LocalizedStringSchema,
    itemText: LocalizedStringSchema,
    itemImageUrl: UrlSchema.optional(),
    /** Screen-reader description — the image inside a reveal panel. Per language, never required,
     *  exempt from the translation gate (OQ-095 D1/D2/D7). */
    itemImageAlt: MediaDescriptionSchema.optional(),
    itemVideoUrl: UrlSchema.optional(),
    /** Screen-reader description — the panel video; the play control speaks this. Per language, never required,
     *  exempt from the translation gate (OQ-095 D1/D2/D7). */
    itemVideoAlt: MediaDescriptionSchema.optional(),
    itemVideoThumbUrl: UrlSchema.optional(),
    subtitlesUrl: LocalizedUrlSchema.optional(),
    questions: z.array(ImageHotspotMiniQuizQuestionSchema).optional(),
})
    .strict();
/**
 * Layout 3.9 — `hidden_items`.
 *
 * Click-to-reveal hotspots over a still image OR a 360° panorama. Two
 * variants discriminated by the presence of `imageUrl` (flat) vs.
 * `image360Url` (360°). The `validateHiddenItemsContent` superRefine
 * applied at the top-level `LayoutContentSchema` union enforces
 * exactly-one-of (you can't ship both; you must ship one).
 *
 * Per catalogue §3.9's Editor controls table.
 *
 * **Why not a discriminated union on `variant`?** Both variants share
 * `type === "hidden_items"` (the Player dispatches on that single type
 * + the presence-of-`image360Url` branch in the runtime). Modelling
 * variants as a `discriminatedUnion("variant", […])` would require an
 * authoring-side field with no XML representation; instead, the
 * variant is inferred from which background-image field is set,
 * matching the Player's own dispatch logic.
 *
 * **Deliberately deferred per Omar 2026-06-02:**
 * - `video360Url` + `video360CoverText` (a third runtime variant —
 *   360° video panorama). Runtime supports it; catalogue §3.9 doesn't
 *   list it; no raw fragment exercises it. Strictly additive future
 *   work when a real course needs it.
 */
export const HiddenItemsContentSchema = z
    .object({
    type: z.literal("hidden_items"),
    titleMain: BlankableLocalizedStringSchema,
    titleSub: LocalizedStringSchema.optional(),
    titleTextColor: ColorSchema,
    titleBackgroundColor: ColorSchema.optional(),
    contentBackgroundColor: ColorSchema.optional(),
    imageUrl: UrlSchema.optional(),
    /** Screen-reader description — the hotspot background a learner is shown. Per language, never required,
     *  exempt from the translation gate (OQ-095 D1/D2/D7). */
    imageAlt: MediaDescriptionSchema.optional(),
    image360Url: UrlSchema.optional(),
    /** Screen-reader description — the panorama; the Player puts it on each hotspot. Per language, never required,
     *  exempt from the translation gate (OQ-095 D1/D2/D7). */
    image360Alt: MediaDescriptionSchema.optional(),
    image360CoverText: LocalizedStringSchema.optional(),
    itemColor: ColorSchema.optional(),
    bgColor: ColorSchema.optional(),
    borderColor: ColorSchema.optional(),
    textColor: ColorSchema.optional(),
    items: z.array(HiddenItemsItemSchema).min(1),
    blockingSection: BlockingSectionSchema,
})
    .strict();
/**
 * Run the variant-presence validation rule on a HiddenItemsContent.
 * Same superRefine factoring as `validateSequenceContent` — kept as a
 * standalone function because `z.discriminatedUnion` rejects ZodEffects
 * members. Applied from the top-level `LayoutContentSchema` union.
 */
function validateHiddenItemsContent(val, ctx) {
    const hasFlat = val.imageUrl !== undefined;
    const has360 = val.image360Url !== undefined;
    if (!hasFlat && !has360) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["imageUrl"],
            message: `\`hidden_items\` requires either \`imageUrl\` (flat 2D variant) or \`image360Url\` (360° panorama variant). Neither is set. See wiki/framework_spec/13-layout-tag-catalogue.md §3.9.`,
        });
    }
    else if (hasFlat && has360) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["image360Url"],
            message: `\`hidden_items\` rejects both \`imageUrl\` and \`image360Url\` set at once — these are mutually exclusive variants. Pick the flat-2D variant (\`imageUrl\` only) or the 360° panorama variant (\`image360Url\` + optional \`image360CoverText\`). See wiki/framework_spec/13-layout-tag-catalogue.md §3.9.`,
        });
    }
    if (val.image360CoverText !== undefined && !has360) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["image360CoverText"],
            message: `\`image360CoverText\` (the "drag to look around" prompt) only applies when \`image360Url\` is set. Remove it for the flat variant.`,
        });
    }
}
/**
 * `HiddenItemsContentSchema` + the variant-presence validation rule.
 * Use this when validating a hidden_items layout in isolation; the
 * top-level `LayoutContentSchema` union runs the same rule automatically.
 */
export const HiddenItemsContentSchemaValidated = HiddenItemsContentSchema.superRefine(validateHiddenItemsContent);
/**
 * Layout 3.11 — `object_viewer` — per-hotspot item.
 *
 * Each hotspot pairs a 3D position on the model surface with a reveal
 * panel that can carry text + optional video. Per catalogue §3.11 + §1.9
 * (shared item/panel block).
 *
 * **`itemPosition` format.** `"x,y,z"` as normalised offsets from the
 * model's centre point. Player runtime at `components.js:5219` parses
 * via `.split(',')` → maps each component to a fraction of the model's
 * half-dimension. Schema treats the string as opaque (same as
 * `HiddenItemsItemSchema.itemPosition`); format validation is out of
 * scope. Negative values are valid (positions on the "back" / "far" side
 * of the model centre).
 *
 * **Why a separate schema from `HiddenItemsItemSchema`?** Both share the
 * §1.9 item/panel block, and since 2026-10-26 they hold the same fields. They
 * stay two schemas because `itemPosition` means different things (a point on a
 * picture, a point on a model), so a constraint added to one must not apply to
 * the other unnoticed.
 *
 * ⚠️ **CORRECTED 2026-10-26 (Plan 37 ②).** This comment previously said
 * `object_viewer` items "cannot carry an `<itemImageUrl>`" (per catalogue
 * §3.11). False since #68 (2026-07-21): the editor offers a picture and its
 * description on every 3D hotspot, and the Player shows them — the panel is
 * built by `_build_hidden_items_descriptions`, shared with hidden_items, which
 * renders `<img src=itemImageUrl alt=itemImageUrl._alt>` (Player `1def193`,
 * `components.js:5508-5509`; measured in Chrome on a desktop and a phone screen,
 * 2026-10-26). With no place here, the clean-up deleted both on every Save.
 *
 * ⚠️ **CORRECTED 2026-08-27.** This comment previously asserted that
 * `object_viewer` items "cannot carry a mini-quiz (`<questions>`)".
 * That was false, and it was load-bearing: the schema had no
 * `questions` seat, so an authored hotspot question was stripped by
 * `sanitizeDraftContent` on save and never reached the package. The
 * runtime builds BOTH layouts' hotspot panels with the SAME helper —
 * `_build_hidden_items_descriptions` (`components.js:5456`), called
 * from `hidden_items_template` (`:5825`) AND `object_viewer_template`
 * (`:5451`) — and hands each panel's `.quizContainer` to the full
 * `quiz_images` behaviour component (`:3829`). So `object_viewer` has
 * always supported a per-hotspot mini-quiz; only the authoring side
 * was missing. The demo corpus is silent on it (no `object_viewer`
 * course authors one), so the CALL GRAPH is the authority here, not
 * the corpus — the same reasoning already recorded for
 * `<videoThumbUrl>` at `emit-layout.ts:1269-1289`.
 *
 * **Deliberately deferred per Omar 2026-06-02 pattern:**
 * - `itemPositionMobile` — same deferred pattern as `HiddenItemsItemSchema`.
 * (`itemImageUrl` and its description, deferred here until 2026-10-26, are in: see the correction above.)
 */
export const ObjectViewerItemSchema = z
    .object({
    itemPosition: z.string(),
    itemTitle: LocalizedStringSchema,
    itemText: LocalizedStringSchema,
    // The panel's picture and its description (Plan 37 ②) — the same fields, in the same order, as
    // `HiddenItemsItemSchema`, because the Player reads both layouts' panels through one helper.
    itemImageUrl: UrlSchema.optional(),
    /** Screen-reader description — the image inside a reveal panel. Per language, never required,
     *  exempt from the translation gate (OQ-095 D1/D2/D7). */
    itemImageAlt: MediaDescriptionSchema.optional(),
    itemVideoUrl: UrlSchema.optional(),
    /** Screen-reader description — the panel video; the play control speaks this. Per language, never required,
     *  exempt from the translation gate (OQ-095 D1/D2/D7). */
    itemVideoAlt: MediaDescriptionSchema.optional(),
    itemVideoThumbUrl: UrlSchema.optional(),
    subtitlesUrl: LocalizedUrlSchema.optional(),
    // Same shape as `HiddenItemsItemSchema.questions`, deliberately the
    // SAME schema object rather than a parallel one: the runtime reads
    // both through one helper, so a second type could only ever drift.
    questions: z.array(ImageHotspotMiniQuizQuestionSchema).optional(),
})
    .strict();
/**
 * Layout 3.11 — `object_viewer`.
 *
 * An interactive 3D model viewer with clickable hotspots on the model
 * surface. Per catalogue §3.11's Editor controls table.
 *
 * Player runtime uses Google's `<model-viewer>` web component
 * (`components.js:5201`); supports `.glb` / `.gltf` / `.mixed.glb`
 * models. Five boolean switches control AR / zoom / auto-rotate / pan /
 * interaction-prompt behaviour at `components.js:5432-5436`.
 *
 * **`objectAltText` + `initialCoverText` are `LocalizedString`.** Matches
 * the `image360CoverText` precedent from PR #41 (hidden_items). Even
 * though the catalogue's widget column says "Plain text input", these
 * strings are user-facing (alt text is read by screen readers; the
 * "drag to look" prompt is shown on the canvas) so localisation matters.
 *
 * **`bgColor` + `borderColor` added 2026-08-27 — the hotspot panel trio
 * was two-thirds present.** This layout's hotspot panels are built by
 * `_build_hidden_items_descriptions(items, layout)`
 * (`dynamo-player/js/components.js:5456`), a helper SHARED with
 * `hidden_items` and called from `object_viewer_template:5451`. It reads
 * `layout.textColor`, `layout.bgColor` AND `layout.borderColor` and
 * writes all three inline on `.itemDescription`. `HiddenItemsContent`
 * carries the whole trio; this schema carried only `textColor`, so the
 * editor's "Panel background" and "Panel border" controls wrote keys the
 * save path silently stripped (`sanitizeDraftContent` drops unrecognized
 * keys rather than refusing) — the author picked a colour, pressed Save,
 * and it was gone. Two layouts sharing one runtime helper must share its
 * contract (`feedback_one_rule_one_place`).
 *
 * The read is invisible to a per-template grep, because the helper is
 * generic and the field name never appears inside `object_viewer_template`
 * (`feedback_a_grep_for_a_name_misses_generic_code`). That is how it was
 * missed, and it is why the fix was measured from the call graph rather
 * than from the template body.
 *
 * ⚠️ CORRECTED 2026-08-28 — this paragraph used to read "`borderColor` is
 * inert on desktop … `.itemDescription` declares no `border` at all, so an
 * inline `border-color` has no width to paint." That is FALSE, and the truth
 * is close to its opposite.
 *
 * `css/Components/components.css` declares, OUTSIDE any media query and so on
 * desktop:
 *
 *   .Component_Module_layouts .object_viewer_template … .itemDescription
 *     { … border-left: 4px solid #ff67ff; … }
 *
 * with the identical rule for `.hidden_items_template`. So there IS a width to
 * paint, and an inline `border-color` recolours a 4px stripe down the panel's
 * left edge — not an outline around it. Where the field really is inert is
 * MOBILE: inside `@media(max-width:1024px)` the same selectors set `border:0`.
 *
 * The old claim was reached by looking for a `border` shorthand and not finding
 * one; `border-left` is what the rule actually uses. Measured by reading each
 * rule with its media context, because the stylesheet is minified and brace
 * splitting alone attributes rules to the wrong media query
 * (`feedback_a_comment_is_not_a_contract`).
 *
 * The Brand screen therefore offers this colour, labelled "Panel left stripe"
 * rather than "Border colour", and says that narrow screens drop it
 * (`packages/schema/src/brand-colour-fields.ts`).
 */
export const ObjectViewerContentSchema = z
    .object({
    type: z.literal("object_viewer"),
    titleMain: BlankableLocalizedStringSchema,
    titleSub: LocalizedStringSchema.optional(),
    titleTextColor: ColorSchema,
    textColor: ColorSchema.optional(),
    bgColor: ColorSchema.optional(),
    borderColor: ColorSchema.optional(),
    backgroundColor: ColorSchema,
    backgroundImageUrl: UrlSchema.optional(),
    objectUrl: UrlSchema,
    objectPosterImgUrl: UrlSchema.optional(),
    objectEnvImgUrl: UrlSchema.optional(),
    objectAltText: LocalizedStringSchema,
    initialCoverText: LocalizedStringSchema.optional(),
    mobileArEnabled: z.boolean().optional(),
    disableZoom: z.boolean().optional(),
    autoRotate: z.boolean().optional(),
    disablePan: z.boolean().optional(),
    disableInteractionPrompt: z.boolean().optional(),
    itemColor: ColorSchema.optional(),
    items: z.array(ObjectViewerItemSchema).min(1),
    blockingSection: BlockingSectionSchema,
})
    .strict();
/**
 * Layouts 3.10 + 3.10b — `quiz_images` + `quiz_gaming` shared shapes.
 *
 * Per catalogue §3.10 + §4.5 + Quiz_layouts_options.md §11. Both
 * layouts share the per-question shape (title block + answers +
 * feedback); `quiz_gaming` adds the DFTI gaming wrapper on top.
 *
 * **Per-answer `feedback` vs. per-question `<feedbackText>`.** The
 * prototype's authoring data (per CLAUDE.md §27 auto-derived feedback
 * rule) stores `feedback` ON EACH answer — single-correct mode. The
 * production XML uses a SINGLE `<feedbackText>` per question (per
 * `raw/ux-references/quiz_images.txt`). The emitter consolidates at
 * emit time: pick the correct answer's feedback as THE feedbackText.
 * Documented in the emitter; out of scope for the schema (which just
 * stores both shapes faithfully).
 */
export const QuizAnswerSchema = z
    .object({
    text: LocalizedStringSchema,
    isCorrect: z.boolean().optional(),
    feedback: LocalizedStringSchema.optional(),
})
    .strict();
/**
 * A discovery hotspot on a quiz question image — `HiddenItemsItemSchema` with
 * `questions` removed, DERIVED from it rather than copied so the two cannot
 * drift (`feedback_one_door_per_field_reuse_existing_style`).
 *
 * ⚠️ **`questions` is omitted because quiz_images cannot render it.**
 * `hidden_items` and `object_viewer` both reach the shared reveal-panel builder
 * `_build_hidden_items_descriptions` (`components.js:5456`), which hands a
 * panel's `.quizContainer` to the full `quiz_images` behaviour component — which
 * is how a hotspot mini-quiz plays there. `quiz_images` has its OWN inline panel
 * builder (`components.js:5686`) that reads `itemTitle` and `itemText` and
 * nothing else. Keeping `questions` would let an author fill a mini-quiz that
 * the emitter would faithfully write and the Player would never show
 * (`feedback_fill_a_box_only_if_what_goes_in_it_ships`) — and it would demand
 * translations, on the Localisation screen, for text no learner can reach.
 */
export const QuizImageHotspotItemSchema = HiddenItemsItemSchema.omit({
    questions: true,
});
/**
 * The per-question media kind, discriminated by `kind`. XML emission
 * wraps in `<content><image|video|html>` per catalogue §1.11.
 *
 * ⚠️ **CORRECTED 2026-08-28.** This comment previously said image-hotspot
 * questions were "deferred … not built in the prototype yet", and that the
 * `items` array was a "future" extension. Both were false, and the falsehood
 * was load-bearing: it is why no seat existed, so `sanitizeDraftContent`
 * deleted every hotspot an author placed and returned 200. The editor has
 * offered a "Hotspots" question kind all along
 * (`layout-editor-bodies.jsx:1578`), seeding `items` plus four colours, and
 * the pinned runtime reads every one of them:
 *
 *   `image.items.item`        → the pins            `components.js:5641`
 *   `image.items.itemColor`   → the pin ring        `components.js:5652`
 *   `image.items.{textColor,bgColor,borderColor}`
 *                             → the reveal panel    `components.js:5686`
 *
 * The same class of defect as the `object_viewer` "cannot carry a mini-quiz"
 * comment corrected on 2026-08-27 — see `feedback_a_comment_is_not_a_contract`.
 *
 * ★ **The item type is `HiddenItemsItemSchema`, deliberately the SAME object**
 * `hidden_items` and `object_viewer` use, not a parallel one. quiz_images reads
 * a strict subset of it (`itemPosition`, `itemTitle`, `itemText`, via its own
 * inline panel builder at `components.js:5686` rather than the shared
 * `_build_hidden_items_descriptions`), so a second type could only ever drift
 * from it (`feedback_one_door_per_field_reuse_existing_style`).
 *
 * ⚠️ **The four colours are FLAT here and NESTED in the XML.** The editor writes
 * them as siblings of `imageUrl` and `normalizeLayoutForSchema`'s flatten
 * (`surface-export.jsx:106-117`) carries them up that way, but the runtime reads
 * them off the `<items>` NODE — `image.items.bgColor`, not `image.bgColor`. The
 * emitter bridges the two shapes; see `emitQuizImageHotspots`. This is unlike
 * `hidden_items`/`object_viewer`, which carry the same trio on the LAYOUT.
 */
export const QuizQuestionMediaSchema = z.discriminatedUnion("kind", [
    z
        .object({
        kind: z.literal("image"),
        imageUrl: UrlSchema,
        /** Screen-reader description — the question image a learner is shown. Per language, never required,
         *  exempt from the translation gate (OQ-095 D1/D2/D7). */
        imageAlt: MediaDescriptionSchema.optional(),
        items: z.array(QuizImageHotspotItemSchema).optional(),
        itemColor: ColorSchema.optional(),
        textColor: ColorSchema.optional(),
        bgColor: ColorSchema.optional(),
        borderColor: ColorSchema.optional(),
    })
        .strict(),
    z
        .object({
        kind: z.literal("video"),
        videoUrl: UrlSchema,
        /** Screen-reader description — the VIDEO carries the description, never its poster (D4). Per language, never required,
         *  exempt from the translation gate (OQ-095 D1/D2/D7). */
        videoAlt: MediaDescriptionSchema.optional(),
        videoThumbUrl: UrlSchema.optional(),
        subtitlesUrl: LocalizedUrlSchema.optional(),
    })
        .strict(),
    z
        .object({
        kind: z.literal("html"),
        htmlUrl: UrlSchema,
        htmlAlt: z.string().optional(),
        /**
         * Per-use tooltip text for templates whose HTML carries `{{slotName}}`
         * placeholders. Map keyed by the template's `TooltipParam.name`;
         * each value is the per-language text the build-step substitutes in
         * at export. Absent → no substitution; empty LocalizedString → the
         * coverage check still flags it as a translation gap.
         *
         * Lives on the `html` variant of `QuizQuestionMediaSchema` (not at
         * the parent QuizQuestion level) to match the prototype's persisted
         * shape — Claude Design's editor writes
         * `{ ...c, tooltipValues: { ... } }` where `c` IS the content object.
         *
         * Permissive at the schema layer: keys are unconstrained because
         * the schema does not know the template registry. The gateway
         * validates `key ∈ template.tooltipParams[].name` at export time.
         */
        tooltipValues: z.record(z.string(), LocalizedStringSchema).optional(),
    })
        .strict(),
]);
/**
 * Per-question shape used by `quiz_images` + `quiz_gaming`.
 *
 * **`content` is REQUIRED** (tightened 2026-06-05 — was `.optional()` from
 * PR #43-era). Per [`wiki/findings/bug-log.md`](../../../Dynamo_Authoring_Wiki/wiki/findings/bug-log.md)
 * 2026-06-04, the Player's `quiz_images_template` at `components.js:5598-5615`
 * unconditionally reads `question.content.video / .image / .html` — a
 * text-only question (no `content` block) causes the entire module to
 * render to a black void with `TypeError: Cannot read properties of
 * undefined (reading 'video')`. 4-sample consensus across
 * `raw/ux-references/`: 24 of 24 real authored questions carry
 * `<content>`. The `optional()` was a schema-side false-positive that
 * the Player has never supported. Documented in
 * `feedback_content_shape_vs_runtime_patch` agent memory.
 */
export const QuizQuestionSchema = z
    .object({
    text: LocalizedStringSchema,
    content: QuizQuestionMediaSchema,
    answers: z.array(QuizAnswerSchema).min(2),
    /**
     * The ONE message the Player shows once this question is answered —
     * `<feedbackText>`, read by the runtime at `components.js:5675-5685`.
     *
     * Added 2026-07-28. It was the only shape a MULTI-CORRECT quiz question
     * could use: with ≥ 2 answers ticked correct, per-answer `feedback` is
     * meaningless (the learner picks a SET), so the authoring UI collapses to
     * one shared message. That message had nowhere to live — the editor wrote
     * `feedbacks: { correct, wrong }` (the SEQUENCE layout's shape), which this
     * `.strict()` object rejected, so the gateway's `sanitizeDraftContent`
     * deleted it on every save and the text never reached a ZIP. Filed in
     * `wiki/findings/2026-07-28-localisation-panel-and-quiz-feedback.md`.
     *
     * Optional: a single-correct question keeps carrying its feedback
     * per-answer, and the emitter still consolidates from the correct answer
     * when this field is absent.
     */
    feedbackText: LocalizedStringSchema.optional(),
})
    .strict();
/**
 * Layout 3.10 — `quiz_images` (non-gamified).
 *
 * A grid-based image quiz: list of questions, each with a piece of
 * media (image / video / HTML) and 2+ answer options. Per catalogue
 * §3.10's Editor controls table.
 *
 * **No DFTI wrapper.** This is the non-gamified variant per CLAUDE.md
 * §23.2 (the prototype's binding rule: two type ids, never an
 * in-editor toggle). The gamified sibling is `QuizGamingContentSchema`
 * below.
 */
export const QuizImagesContentSchema = z
    .object({
    type: z.literal("quiz_images"),
    titleMain: BlankableLocalizedStringSchema,
    titleSub: LocalizedStringSchema.optional(),
    titleTextColor: ColorSchema,
    titleBackgroundColor: ColorSchema.optional(),
    contentBackgroundColor: ColorSchema.optional(),
    contentTextColor: ColorSchema.optional(),
    selectedBgColor: ColorSchema.optional(),
    selectedTextColor: ColorSchema.optional(),
    dotSelectedColor: ColorSchema.optional(),
    questions: z.array(QuizQuestionSchema).min(1),
    blockingSection: BlockingSectionSchema,
})
    .strict();
/**
 * Sub-schema §4.5 — DFTI gaming-header counter strip.
 *
 * Per catalogue §4.5 + the prototype's `gaming_header` shape. The XML
 * encodes `enabled` as the element's text content (`true` / `false`)
 * and `imgUrlCorrect` / `imgUrlWrong` as attributes:
 *
 * ```xml
 * <dfti_header imgUrlCorrect="..." imgUrlWrong="...">true</dfti_header>
 * ```
 *
 * **Path convention — FULL path (no Player auto-prefix).** `imgUrlCorrect`
 * and `imgUrlWrong` MUST carry the full path the browser will see, e.g.
 * `content/dfti/images/robot_icon_big.png` — NOT bare. The Player renders
 * the header icons via raw `<img src="${dftiHeader._imgUrlCorrect}">` in
 * `components.js:5596` with no path resolver. This is the OPPOSITE
 * convention to top-level `dftiLeftImg` / `dftiRightImg` (which the
 * Player auto-prefixes with `content/`, so those carry bare paths). Bare
 * paths here produce 404s for the top-right scoreboard icons. See the
 * `feedback_player_path_prefix_per_field` agent memory for the full
 * per-field path-prefix table, and `wiki/findings/bug-log.md`
 * (2026-06-05 entry, sub-gotcha A) for the diagnosis.
 *
 * **The icons are never required.** The Player reads them only when the
 * header is on, and with the header on and no icons it shows the two
 * counters with no icon beside them (measured 2026-10-13). Until then a
 * save refused that state; Omar removed the rule with every other content
 * check (Q5).
 */
export const GamingHeaderSchema = z
    .object({
    enabled: z.boolean(),
    imgUrlCorrect: UrlSchema.optional(),
    imgUrlWrong: UrlSchema.optional(),
})
    .strict();
/**
 * Sub-schema §4.5 — DFTI start / win / fail screen content.
 *
 * Per catalogue §4.5. The XML encodes `title` / `titleColor` /
 * `bgColor` / `bgImgUrl` as attributes; the element's CDATA body
 * (`body`) holds the prose.
 *
 * **Per-screen token-substitution surface (load-bearing — `body` only).**
 * The schema is identical for start / win / fail screens, but the Player
 * runtime substitutes a DIFFERENT set of placeholder tokens in the
 * `body` depending on which screen it's used for. The substitution
 * happens at `components.js:5596` via chained `.replace()` calls; the
 * start-screen chain is shorter than the end-screen one:
 *
 * | Used as… | `body` substitutes |
 * |---|---|
 * | `gamingStartScreen.body` | `{img1}`, `{img2}`, `{img3}` ONLY |
 * | `gamingEndScreens.win.body` | `{img1/2/3}` + `{neededAnswers}` + `{totalAnswers}` + `{wrongAnswers}` |
 * | `gamingEndScreens.failure.body` | Same 6 tokens as `win.body` |
 * | `title` (any screen) | No substitution — plain text only |
 *
 * Authors who drop `{totalAnswers}` (or `{neededAnswers}` /
 * `{wrongAnswers}`) into a `gamingStartScreen.body` will see the literal
 * `{totalAnswers}` string at runtime — it never substitutes on the start
 * screen. Use literal numbers on the start screen ("Answer 3 questions
 * to pass"); reserve the answer-count tokens for end-screen bodies. See
 * `wiki/findings/bug-log.md` (2026-06-05 entry, sub-gotcha B) for the
 * full runtime trace.
 */
export const GamingScreenSchema = z
    .object({
    title: LocalizedStringSchema,
    body: LocalizedStringSchema,
    titleColor: ColorSchema.optional(),
    bgColor: ColorSchema.optional(),
    bgImgUrl: UrlSchema.optional(),
})
    .strict();
/**
 * Sub-schema §4.5 — DFTI end-screens pair (win + failure).
 *
 * The XML emits two `<dfti_endScreen>` elements — one with no `for`
 * attribute (the win screen) and one with `for="failure"` (the fail
 * screen). The schema models them as named keys (`win` + `failure`)
 * for cleaner authoring; the emitter converts to the attribute-keyed
 * XML shape.
 */
export const GamingEndScreensSchema = z
    .object({
    win: GamingScreenSchema,
    failure: GamingScreenSchema,
})
    .strict();
/**
 * Layout 3.10b — `quiz_gaming` (DFTI variant).
 *
 * Same per-question shape as `QuizImagesContentSchema`, plus the DFTI
 * gaming wrapper (header counter, start screen, end-screen pair, pass
 * threshold, post-assessment + show-correct-wrong toggles).
 *
 * **Type-id strategy.** Per Quiz_layouts_options.md §11.2 Q1 (Omar's
 * locked-in decision 2026-06-02): two separate layout type ids
 * (`quiz_images` non-gamified + `quiz_gaming` gamified). Both emit
 * `<type>quiz_images</type>` at the XML level; only `quiz_gaming`
 * also emits the DFTI wrapper tags. The discriminated union routes
 * by `type` per the standard pattern.
 *
 * **Field-naming divergence from §0.3.** Catalogue §0.3 says "field
 * name = XML tag name in camelCase". For DFTI fields the prototype
 * convention (CLAUDE.md §23.2 — "Never write 'DFTI' in author-facing
 * data; use `gaming_*`") takes precedence. So `gamingHeader` ↔
 * `<dfti_header>`, `gamingStartScreen` ↔ `<dfti_startScreen>`,
 * `gamingEndScreens.{win, failure}` ↔ `<dfti_endScreen>` × 2. The
 * emitter does the translation.
 *
 * **`correctAnswersNeeded` validation.** Must be ≥ 1 and ≤ questions
 * length. Enforced via `superRefine` on `LayoutContentSchema` (factored
 * out for the same ZodEffects-vs-discriminatedUnion reason as
 * `validateSequenceContent` / `validateHiddenItemsContent`).
 *
 * **Course-level Profile form NOT modelled here.** Per the prototype
 * iteration status (2026-06-03 re-audit), the course-level
 * `courseSettings.dftiFlow` slice (master enable + form labels + intro
 * screen images) is pending the §11.5 follow-up prototype iteration.
 * When that ships, it'll be a new slice on `course.ts`, NOT additional
 * fields on this per-layout schema. Per-layout `quiz_gaming` is
 * complete and shippable independently; the course-level Profile form
 * ships in a follow-up PR.
 */
export const QuizGamingContentSchema = z
    .object({
    type: z.literal("quiz_gaming"),
    titleMain: BlankableLocalizedStringSchema,
    titleSub: LocalizedStringSchema.optional(),
    titleTextColor: ColorSchema,
    titleBackgroundColor: ColorSchema.optional(),
    contentBackgroundColor: ColorSchema.optional(),
    contentTextColor: ColorSchema.optional(),
    selectedBgColor: ColorSchema.optional(),
    selectedTextColor: ColorSchema.optional(),
    dotSelectedColor: ColorSchema.optional(),
    questions: z.array(QuizQuestionSchema).min(1),
    showCorrectWrongAnswers: z.boolean(),
    countAsPostAssessment: z.boolean(),
    correctAnswersNeeded: z.number().int().positive(),
    gamingHeader: GamingHeaderSchema,
    gamingStartScreen: GamingScreenSchema,
    gamingEndScreens: GamingEndScreensSchema,
    blockingSection: BlockingSectionSchema,
})
    .strict();
/*
 * No save-time rule for a gaming quiz any more (Omar, 2026-10-13, removing every content check —
 * his Q5). The two it had are gone: a pass mark above the number of questions, and a score header
 * switched on without its two counter icons. Both were measured in Chrome on the pinned Player
 * (wiki/findings/2026-10-13-remove-content-checks-explore.md): the first gives "Not this time" on
 * every attempt — the learner still moves on, unless the quiz counts as the post-assessment — and
 * the second shows the two counters with no icon beside them.
 */
/**
 * The discriminated union over every layout type. Discriminator: `type`.
 *
 * Today: 14 variants (`fullscreen_text_and_image`, `title`,
 * `two_columns_text`, `text_and_image`, `small_video`, `fullscreen_video`,
 * `sequence`, `vertical_tabs`, `icons_discover`, `horizontal_tabs`,
 * `hidden_items`, `object_viewer`, `quiz_images`, `quiz_gaming`). The
 * union widens as variants are added; the emitter switch fails
 * compilation when a variant is added without a matching case (the
 * desired forcing function).
 *
 * **13 of 13 backend layouts shipped** with this PR — the production
 * Player runtime registers 13 distinct `<type>` values
 * (`mandatoryQuestion` / `discover` / `question` standalone are NOT in
 * the dispatcher; they only render as in-video interactions). The two
 * `quiz_*` types both emit `<type>quiz_images</type>` at the XML level
 * but only `quiz_gaming` adds the DFTI wrapper tags.
 */
export const LayoutContentSchema = z
    .discriminatedUnion("type", [
    FullscreenTextAndImageContentSchema,
    TitleContentSchema,
    TwoColumnsTextContentSchema,
    TextAndImageContentSchema,
    SmallVideoContentSchema,
    FullscreenVideoContentSchema,
    SequenceContentSchema,
    VerticalTabsContentSchema,
    IconsDiscoverContentSchema,
    HorizontalTabsContentSchema,
    HiddenItemsContentSchema,
    ObjectViewerContentSchema,
    QuizImagesContentSchema,
    QuizGamingContentSchema,
])
    .superRefine((val, ctx) => {
    // Per-layout schema-side validation rules. Discriminator routes here.
    if (val.type === "sequence") {
        validateSequenceContent(val, ctx);
    }
    else if (val.type === "hidden_items") {
        validateHiddenItemsContent(val, ctx);
    }
});
//# sourceMappingURL=layouts.js.map